import { format as formatDateFns } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { type PXTime } from 'infra/interfaces/candidate';
import { deconstructDateByMilliseconds } from '../date';

const DIACRITICS_REGEX = /[̀-ͯ]/g;

// Building an Intl formatter costs ~45x more than reusing one, and these run in
// list cards on every render. Cache one instance per key (`undefined` included),
// created on first call: an option the browser lacks (e.g. `roundingMode`) then
// throws inside that formatter only, instead of breaking this module's import.
const memoizeFormatter = <K, F>(create: (key?: K) => F) => {
	const cache = new Map<K | undefined, F>();

	return (key?: K): F => {
		const cached = cache.get(key);
		if (cached) return cached;

		const formatter = create(key);
		cache.set(key, formatter);
		return formatter;
	};
};

export const normalizeDiacritics = (text: string) =>
	text.normalize('NFD').replace(DIACRITICS_REGEX, '').toLowerCase();

export const formatCapitalizeFirstLetter = (keyword: string) => {
	if (!keyword || keyword.trim() === '') return '';
	const keywordTrim = keyword.replace(/\s+/g, ' ').trim();
	const words = keywordTrim.split(' ');
	const formatedWords = words.map(
		(word) => word[0].toUpperCase() + word.substring(1).toLowerCase(),
	);
	return formatedWords.join(' ');
};

export const formatSentenceCase = (text: string) => {
	if (!text || text.trim() === '') return '';
	const lower = text.replace(/\s+/g, ' ').trim().toLowerCase();
	return lower.replace(/(^\s*\p{L}|[/–—-]\s*\p{L})/gu, (match) =>
		match.toUpperCase(),
	);
};

export const formatCPF = (text: string) => {
	const cleaned = text.replace(/\D/g, '').slice(0, 11);

	if (!cleaned) return '';

	const part1 = cleaned.slice(0, 3);
	const part2 = cleaned.slice(3, 6);
	const part3 = cleaned.slice(6, 9);
	const part4 = cleaned.slice(9, 11);

	let result = part1;
	if (part2) result += `.${part2}`;
	if (part3) result += `.${part3}`;
	if (part4) result += `-${part4}`;

	return result;
};

export const formatCNPJ = (text: string) => {
	const cleaned = text
		.replace(/[^A-Za-z0-9]/g, '')
		.toUpperCase()
		.slice(0, 14);

	if (!cleaned) return '';

	const part1 = cleaned.slice(0, 2);
	const part2 = cleaned.slice(2, 5);
	const part3 = cleaned.slice(5, 8);
	const part4 = cleaned.slice(8, 12);
	const part5 = cleaned.slice(12, 14);

	let result = '';
	if (part1) result = part1;
	if (part2) result += `.${part2}`;
	if (part3) result += `.${part3}`;
	if (part4) result += `/${part4}`;
	if (part5) result += `-${part5}`;

	return result;
};

export const formatPhone = (text: string, removeCountryCode = false) => {
	let formattedText = text
		.replace('+55', '')
		.replace(/\D/g, '')
		.replace(/(\d{0})(\d)/, '+55 ($2')
		.replace(/(\d{2})(\d)/, '$1) $2')
		.replace(/(\) \d)(\d)/, '$1 $2')
		.replace(/(\d{4})(\d)/, '$1-$2')
		.replace(/(-\d{4})\d+?$/, '$1');

	if (removeCountryCode) {
		formattedText = formattedText.replace('+55 ', '');
	}

	return formattedText;
};

export const formatWhatsappPhone = (phone: string) => {
	const numbers = formatNumber(phone);
	return numbers.length <= 11 ? `55${numbers}` : numbers;
};

export const formatPhoneOnlyVisual = (text: string): string => {
	const digits = text.replace(/\D/g, '');
	if (!digits) return '';

	const ddi = digits.slice(0, 2);
	const ddd = digits.slice(2, 4);
	const first = digits.slice(4, 5);
	const part1 = digits.slice(5, 9);
	const part2 = digits.slice(9, 13);

	if (digits.length <= 2) {
		return `+${ddi}`;
	}

	if (digits.length <= 4) {
		return `+${ddi} (${ddd}`;
	}

	if (digits.length <= 5) {
		return `+${ddi} (${ddd}) ${first}`;
	}

	if (digits.length <= 9) {
		return `+${ddi} (${ddd}) ${first} ${part1}`;
	}

	return `+${ddi} (${ddd}) ${first} ${part1}-${part2}`;
};

export const formatDate = (
	date: Date | string | null | undefined,
	format = 'dd/MM HH:mm',
	isHiddenDate = false,
) => {
	if (isHiddenDate) {
		return format.replace(/[a-zA-Z]/g, '-');
	}

	if (!date) return '';

	if (typeof date !== 'string') {
		return formatDateFns(date, format, { locale: ptBR });
	}

	if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
		const [year, month, day] = date.split('-');
		const dateFormatted = new Date(
			Number(year),
			Number(month) - 1,
			Number(day),
		);

		return formatDateFns(dateFormatted, format, { locale: ptBR });
	}

	return formatDateFns(new Date(date), format, { locale: ptBR });
};

export const formatDateSafe = (
	date: Date | string | undefined | null,
	format = 'dd/MM/yyyy',
) => {
	if (!date) return '--';
	return formatDate(date, format);
};

export const formatCivilDate = (
	date: string | undefined | null,
	format = 'dd/MM/yyyy',
) => {
	if (!date) return '--';

	const [civilDate] = date.split('T');
	return formatDate(civilDate, format);
};

export const formatWeekdayName = (date: Date | string | null | undefined) =>
	formatDate(date, 'EEEE').replace(/-feira$/, '');

const getPriceFormatter = memoizeFormatter(
	(decimals?: number) =>
		new Intl.NumberFormat('pt-BR', {
			style: 'currency',
			currency: 'BRL',
			...(decimals !== undefined && {
				minimumFractionDigits: decimals,
				maximumFractionDigits: decimals,
			}),
		}),
);

const HIDDEN_PRICE = 'R$ ••••••';

type FormatPriceOptions = {
	/** Returns a fixed mask instead of the price, whatever the value. */
	hidden?: boolean;
	decimals?: number;
};

/** Formats cents as BRL. The single place that decides how a hidden price looks. */
export const formatPrice = (
	value = 0,
	{ hidden = false, decimals }: FormatPriceOptions = {},
): string => {
	if (hidden) return HIDDEN_PRICE;

	return getPriceFormatter(decimals)
		.format(value / 100)
		.replace(/\s/, ' ');
};

export const toDisplayPercentage = (value: number): number => value / 100;

const getPercentFormatter = memoizeFormatter(
	(decimals?: number) =>
		new Intl.NumberFormat('pt-BR', {
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		}),
);

export const formatPercent = (value = 0, decimals?: number): string => {
	return getPercentFormatter(decimals).format(value) + '%';
};

export const formatPlate = (text: string) => {
	return text
		.trim()
		.slice(0, 8)
		.replace(/([a-zA-Z]{3})([0-9]{1})([a-zA-Z0-9]{1})([0-9]{2})/g, '$1-$2$3$4')
		.toUpperCase();
};

export const formatPlateValidated = (text: string): string | null => {
	const raw = text
		.trim()
		.replace(/[^a-zA-Z0-9]/g, '')
		.toUpperCase();

	if (/^[A-Z]{3}[0-9][A-Z][0-9]{2}$/.test(raw)) {
		return raw;
	}

	if (/^[A-Z]{3}[0-9]{4}$/.test(raw)) {
		return `${raw.slice(0, 3)}-${raw.slice(3)}`;
	}

	return null;
};

export const formatHourMask = (text: string): string => {
	const digits = text.replace(/\D/g, '').slice(0, 4);
	if (digits.length < 2) return digits;

	const validTwoDigitHour = Number(digits.slice(0, 2)) <= 23;
	const hourDigits = validTwoDigitHour
		? digits.slice(0, 2)
		: digits.slice(0, 1);
	const hour = hourDigits.padStart(2, '0');
	const minuteDigits = digits.slice(hourDigits.length, hourDigits.length + 2);

	if (minuteDigits.length === 0) return hour;
	if (minuteDigits.length < 2) return `${hour}:${minuteDigits}`;
	const minute = Math.min(Number(minuteDigits), 59).toString().padStart(2, '0');
	return `${hour}:${minute}`;
};

export const completeIncompleteHour = (value: string): string | null => {
	const digits = value.replace(/\D/g, '');
	if (digits.length === 0 || digits.length > 3) return null;
	if (digits.length <= 2) return `${digits.padStart(2, '0')}:00`;

	const [hourPart, minutePart] = value.split(':');
	if (minutePart !== undefined && hourPart.startsWith('0')) {
		return `${hourPart}:${minutePart.padStart(2, '0')}`;
	}

	const hour = digits.slice(0, -2).padStart(2, '0');
	const minute = Math.min(Number(digits.slice(-2)), 59)
		.toString()
		.padStart(2, '0');
	return `${hour}:${minute}`;
};

export const formatCEP = (text: string) => {
	return text
		.replace(/\D/g, '')
		.replace(/(\d{5})(\d)/, '$1-$2')
		.replace(/(-\d{3})\d+?$/, '$1');
};

export const formatNumber = (text: string) => {
	if (text.trim() === '') return '';
	return text.replace(/\D/g, '');
};

export const formatCurrency = (text: string): string => {
	if (text.trim() === '') return '';
	const signal = text[0] === '-' ? '-' : '';
	const formatedNumber = text
		.replace(/\D/g, '')
		.replace(/(\d)(\d{2})$/, '$1,$2')
		.replace(/(?=(\d{3})+(\D))\B/g, '.');
	return signal + formatedNumber;
};

export const formatSlug = (text: string) => {
	return text
		.toLowerCase()
		.trim()
		.replace(/[àÀáÁâÂãäÄÅåª]+/g, 'a')
		.replace(/[èÈéÉêÊëË]+/g, 'e')
		.replace(/[ìÌíÍîÎïÏ]+/g, 'i')
		.replace(/[òÒóÓôÔõÕöÖº]+/g, 'o')
		.replace(/[ùÙúÚûÛüÜ]+/g, 'u')
		.replace(/[ýÝÿŸ]+/g, 'y')
		.replace(/[ñÑ]+/g, 'n')
		.replace(/[çÇ]+/g, 'c')
		.replace(/[ß]+/g, 'ss')
		.replace(/[Ææ]+/g, 'ae')
		.replace(/[Øøœ]+/g, 'oe')
		.replace(/[%]+/g, 'pct')
		.replace(/\s+/g, '-')
		.replace(/[^\w\s-]/g, '')
		.replace(/[\s_-]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.replace(/^-+/, '')
		.replace(/-+$/, '');
};

export const formatLimitCharacters = (text: string, limit = 13) => {
	if (!text) return '';
	if (text.length > limit) return `${text.slice(0, limit)}...`;
	return text;
};

export const formatPlates = (text: string) => {
	return text
		.split(',')
		.map((plate: string) => formatPlate(plate))
		.join(', ')
		.trim();
};

export const formatDayCount = (days?: number | null) => {
	return (days ?? 0).toString().padStart(2, '0');
};

export const formatDayLabel = (count: number) => {
	return count === 1 ? 'dia' : 'dias';
};

export const pluralizeCount = (
	count: number | undefined,
	singular: string,
	plural: string,
) => [count || null, count === 1 ? singular : plural].filter(Boolean).join(' ');

export const formatPxTime = (time: PXTime): string => {
	const parts: string[] = [];

	if (time?.years > 0) {
		const yearText = time.years === 1 ? '1 ano' : `${time.years} anos`;
		parts.push(yearText);
	}

	if (time?.months > 0) {
		const monthText = time.months === 1 ? '1 mês' : `${time.months} meses`;
		parts.push(monthText);
	}

	if (time?.days > 0) {
		const dayText = time.days === 1 ? '1 dia' : `${time.days} dias`;
		parts.push(dayText);
	}

	if (parts.length <= 1) return parts.join('');

	return `${parts.slice(0, -1).join(' ')} e ${parts[parts.length - 1]}`;
};

export const formatExtendedDateMask = (params: {
	milliseconds: number;
	year: string;
	years: string;
	month: string;
	months: string;
	and: string;
}) => {
	if (params.milliseconds <= 0) return `0 ${params.months}`;
	const { years, months, days } = deconstructDateByMilliseconds(
		params.milliseconds,
	);

	const yearsPlural = years > 1 ? params.years : params.year;
	const yearText = years > 0 ? `${years} ${yearsPlural} ${params.and} ` : '';
	const monthRound = days > 15 ? months + 1 : months + 0.5;
	const monthText = monthRound === 1 ? params.month : params.months;

	return `${yearText}${monthRound} ${monthText}`.replace('.', ',');
};

export const formatDistance = (distanceInKm?: number | null): string => {
	if (distanceInKm == null || isNaN(distanceInKm)) {
		return '-';
	}

	if (distanceInKm < 1) {
		const distanceInMeters = Math.round(distanceInKm * 1000);
		return `${distanceInMeters} mt`;
	}
	return `${distanceInKm} km`;
};

const getPriceCompactFormatter = memoizeFormatter(
	() =>
		new Intl.NumberFormat('pt-BR', {
			notation: 'compact',
			compactDisplay: 'short',
			minimumFractionDigits: 0,
			maximumFractionDigits: 1,
			roundingMode: 'trunc',
		}),
);

export const formatPriceCompact = (value = 0) => {
	const formattedValue = value / 100;

	return getPriceCompactFormatter().format(Number(formattedValue));
};

const getReviewsCountFormatter = memoizeFormatter(
	() => new Intl.NumberFormat('pt-BR'),
);

export const formatReviewsCount = (value: number | string) => {
	const count = Number(value);

	return Number.isFinite(count)
		? getReviewsCountFormatter().format(count)
		: '0';
};

export const formatRating = (value: string | number) => {
	value = Number(value);
	value = Number.isNaN(value) ? 0 : value;

	return value.toFixed(2).replace('.', ',');
};

const getDecimalFormatter = memoizeFormatter(
	(decimals?: number) =>
		new Intl.NumberFormat('pt-BR', {
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		}),
);

export const formatDecimal = (value: number | string, decimals = 2) => {
	return getDecimalFormatter(decimals).format(Number(value));
};

const safeDivide = (numerator: number, denominator: number): number => {
	if (denominator === 0) {
		return 0;
	}
	return numerator / denominator;
};

const safeMultiply = (value: number, factor: number = 100): number => {
	return value * factor;
};

export const toPercentage = (amount: number, total: number): number =>
	safeMultiply(safeDivide(amount, total));
